import type { KeyboardEvent } from 'react'

import { useRef, useState } from 'react'

import type { ColorGetter } from '../system'
import type { Dict } from '../utils'
import type { RatingProps } from './rating.types'

import Box from '../box'
import { vui } from '../core'
import Icon from '../icon'
import { mergeRefs } from '../utils'
import theme from './theme'

export const Rating = vui<'div', RatingProps>((props, ref) => {
  const {
    value: propValue = 0,
    isDisabled = false,
    max: propMax,
    onChange,
    showAll: showAllProp = true,
    variant = 'yellow',
    size,
    ...rest
  } = props
  const [hoverValue, setHoverValue] = useState<number | null>(null)
  const [internalValue, setInternalValue] = useState(propValue || 0)
  const [focusedStar, setFocusedStar] = useState<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const isInteractive = !isDisabled && onChange !== undefined
  const showAll = showAllProp || isInteractive

  const { variants } = (theme as Dict) ?? {}
  const color = isDisabled ? 'sandstone.79' : variants[variant].color

  if (propValue < 0) {
    console.error(`Rating: value (${propValue}) cannot be negative`)
  }

  const value = Math.max(0, propValue)
  const max = propMax ? propMax : Math.max(value, 5)

  if (value > max) {
    console.error(`Rating: value (${value}) cannot be greater than max (${max})`)
  }

  const handleValueChange = (newValue: number) => {
    if (!isInteractive) return
    const clampedValue = Math.max(0, Math.min(newValue, max))
    setInternalValue(clampedValue)
    onChange?.(clampedValue)
  }

  const handleStarClick = (clickedValue: number) => {
    if (!isInteractive) return
    const newValue = clickedValue === internalValue ? 0 : clickedValue
    handleValueChange(newValue)
  }

  const handleStarHover = (hoveredValue: number) => {
    if (!isInteractive) return
    setHoverValue(hoveredValue)
  }

  const handleMouseLeave = () => {
    if (!isInteractive) return
    setHoverValue(null)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isInteractive) return
    const currentValue = propValue !== undefined ? propValue : internalValue
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        event.preventDefault()
        handleValueChange(Math.min(currentValue + 1, max))
        break
      case 'ArrowLeft':
      case 'ArrowDown':
        event.preventDefault()
        handleValueChange(Math.max(currentValue - 1, 0))
        break
      case 'Home':
        event.preventDefault()
        handleValueChange(0)
        break
      case 'End':
        event.preventDefault()
        handleValueChange(max)
        break
    }
  }

  const handleFocus = () => {
    if (!isInteractive) return
    if (focusedStar === null) {
      setFocusedStar(propValue !== undefined ? propValue : internalValue)
    }
  }

  const handleBlur = () => {
    if (!isInteractive) return
    setFocusedStar(null)
  }

  const displayValue = hoverValue !== null ? hoverValue : value !== undefined ? value : internalValue

  return (
    <Box
      aria-label="Rating"
      aria-valuemax={max}
      aria-valuemin={0}
      aria-valuenow={displayValue}
      className="vui-rating"
      focusRing={3}
      focusRingColor="focusColor"
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      onMouseLeave={handleMouseLeave}
      outline="none"
      ref={mergeRefs(ref, containerRef)}
      role="slider"
      tabIndex={isInteractive ? 0 : -1}
      {...rest}
    >
      {Array.from({ length: max }).map((_, index) => {
        const starValue = index + 1
        const fullStar = index < displayValue
        return (
          <Box
            className="vui-rating-star"
            cursor={isInteractive ? 'pointer' : undefined}
            gap="1px"
            key={index}
            onClick={() => handleStarClick(starValue)}
            onFocus={() => setFocusedStar(starValue)}
            onMouseEnter={() => handleStarHover(starValue)}
            tabIndex={-1}
          >
            {fullStar ? (
              <FullStar color={color} size={size} />
            ) : showAll ? (
              <EmptyStar color="sandstone.79" size={size} />
            ) : null}
          </Box>
        )
      })}
    </Box>
  )
})

const FullStar = (props: { color: ColorGetter; size: RatingProps['size'] }) => (
  <Icon color={props.color} name="uiStar" size={props.size} />
)
const EmptyStar = (props: { color: ColorGetter; size: RatingProps['size'] }) => (
  <Icon color={props.color} name="uiStar" size={props.size} />
)

Rating.displayName = 'Rating'
export default Rating
